import React, { useState } from 'react';
import { AppBar, Toolbar, Box, Typography, IconButton, Avatar, Button, useMediaQuery, useTheme } from '@mui/material';
import { Menu as MenuIcon, AccountBalance, Home, Description, Info, HelpOutline, Feedback } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';
import { FeedbackModal } from './FeedbackModal';

interface NavbarProps {
  onMenuClick: () => void;
  isSidebarOpen?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ onMenuClick, isSidebarOpen = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const navItems = [
    { label: t('home'), path: '/', icon: <Home /> },
    { label: t('government_schemes'), path: '/schemes', icon: <AccountBalance /> },
    { label: t('feedback'), path: null, icon: <Feedback />, action: () => setFeedbackModalOpen(true) },
    { label: t('contact_support'), path: '/contact', icon: <HelpOutline /> },
    { label: t('about'), path: '/about', icon: <Info /> },
  ];

  const handleNavClick = (path: string | null, action?: () => void) => {
    if (action) {
      action();
    } else if (path) {
      navigate(path);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        background: 'linear-gradient(135deg, #673AB7 0%, rgba(103,58,183,0.7) 100%)',
        zIndex: (theme) => theme.zIndex.drawer + 1 
      }}
    >
      <Toolbar>
        {/* Mobile Menu Icon */}
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        {/* Logo and App Title */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            mr: { xs: 1, md: 4 },
            flex: { xs: 1, sm: 'initial' },
            minWidth: 0
          }}
          onClick={() => navigate('/')}
        >
          <Avatar 
            src="/icon.jpg"
            alt="Nagrik Sahayak Logo"
            sx={{ 
              width: { xs: 32, sm: 40 }, 
              height: { xs: 32, sm: 40 }, 
              mr: { xs: 0.75, sm: 1.5 },
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              flexShrink: 0
            }}
          >
            <AccountBalance />
          </Avatar>
          
          <Typography 
            component="div" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1.25rem' },
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {t('nagrik_sahayak')}
          </Typography>
        </Box>
        
        {/* Spacer to push navigation to the right - adjusted for mobile */}
        <Box sx={{ flexGrow: { xs: 0, sm: 1 } }} />
        
        {/* Desktop Navigation Links */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
            {navItems.map((item) => {
              const isActive = item.path && location.pathname === item.path;
              return (
                <Button
                  key={item.label}
                  onClick={() => handleNavClick(item.path, item.action)}
                  startIcon={item.icon}
                  sx={{
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 400,
                    bgcolor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&::after': isActive ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: 3,
                      bgcolor: 'white',
                      borderRadius: '3px 3px 0 0',
                    } : {}
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        )}
        
        {/* Language Switcher - Hidden when sidebar is open */}
        {!isSidebarOpen && <LanguageSwitcher />}
      </Toolbar>

      {/* Feedback Modal */}
      <FeedbackModal 
        open={feedbackModalOpen} 
        onClose={() => setFeedbackModalOpen(false)} 
      />
    </AppBar>
  );
};
